import { useCallback } from 'react';

import { useAppSelector } from '.';

/**
 * Translates quality level labels for display in the settings menu.
 * Only applies translation for non-webcast content (on-demand videos).
 * 
 * @param levels - Array of quality levels with labels
 * @param currentQuality - Index of the quality level being labeled
 * @returns Translated label string (Auto, High Quality, Medium Quality, Data Saver) or original label
 */
function translateQualityLabel(
    levels: { label: string }[],
    currentQuality: number
): string {
    const current = levels[currentQuality];
    if (!current) return '';

    if (current.label.toLowerCase() === 'auto') {
        return 'Auto';
    }

    const hasAuto = levels[0]?.label.toLowerCase() === 'auto';
    const effectiveLevels = hasAuto ? levels.slice(1) : levels;

    const index = hasAuto ? currentQuality - 1 : currentQuality;
    const total = effectiveLevels.length;

    if (total === 3) {
        return ['High Quality', 'Medium Quality', 'Data Saver'][index] ?? 'Data Saver';
    }

    if (index === 0) return 'High Quality';
    if (index === total - 1) return 'Data Saver';
    return 'Medium Quality';
}

/**
 * Hook to manage quality label translation for player settings menu.
 * Provides utilities to translate technical quality labels (e.g., "720p (1396 kbps)")
 * into user-friendly labels (e.g., "High Quality") for on-demand content only.
 * 
 * @returns Object with shouldTranslate flag and getQualityLabel function
 */
export const useQualityLabels = () => {
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    
    // Only translate for on-demand content (not webcasts/live streams)
    const shouldTranslate = !launchDetails?.isWebcast;

    /**
     * Gets the appropriate quality label based on content type.
     * Returns translated label for on-demand, original label for webcasts.
     * 
     * @param levels - Array of quality levels
     * @param currentQuality - Index of current quality level
     * @returns Translated or original label
     */
    const getQualityLabel = useCallback((
        levels: { label: string }[],
        currentQuality: number
    ): string => {
        if (!shouldTranslate) {
            return levels[currentQuality]?.label ?? '';
        }
        return translateQualityLabel(levels, currentQuality);
    }, [shouldTranslate]);

    return {
        shouldTranslate,
        getQualityLabel,
    };
};